/** Stands in for the collapsed conversations panel (APP-3397 / APP-3413). A flex child, never an overlay. */

import { useCallback, useEffect, useRef, useState } from "react"
import type { Ref } from "react"
import { Add, AdminPanelSettingsOutlined, EventRepeatOutlined, ExtensionOutlined, GridViewOutlined, ViewSidebarOutlined } from "@mui/icons-material"
import { IconButton, Paper, Tooltip } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { AI_TEAMMATE_EVENTS } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import { SKILL_EVENTS } from "connected-components/skills/analytics/events"
import { useAppDispatch } from "hooks/redux"
import { ADMIN_SETTINGS_EVENTS } from "../admin-settings/events"
import { closeDockPane } from "../store/agent-chat-slice"
import { ConversationsPreview } from "./conversations-preview"
import { useKellyPanel } from "./kelly-chat-context"
import type { SkillsEntryProps, SurfaceNav } from "./side-panel"
import { useHoverPreview } from "./use-hover-preview"

const RAIL_SX = { flexShrink: 0, width: (theme: Theme) => theme.spacing(6.5), borderRight: 1, borderColor: "divider", display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5, p: 0.5, minHeight: 0 } satisfies SxProps<Theme>
const RAIL_BUTTON_SX = { color: "var(--app-text-secondary)", "&:hover": { color: "var(--app-text-primary)", backgroundColor: "var(--app-subtle-hover-surface)" } } satisfies SxProps<Theme>
const RAIL_BUTTON_ACTIVE_SX = { ...RAIL_BUTTON_SX, bgcolor: "action.selected" } satisfies SxProps<Theme>

interface CollapsedRailProps {
  onOpen: () => void
  /** Focus target, so collapsing the panel lands focus here. */
  openButtonRef?: Ref<HTMLButtonElement>
  skills?: SkillsEntryProps
  /** Admins only: hosts omit it for every other role, as for the side panel. */
  adminSettings?: SkillsEntryProps
  surfaceNav?: SurfaceNav
}

export function CollapsedRail({ onOpen, openButtonRef, skills, adminSettings, surfaceNav }: CollapsedRailProps) {
  const { startNewThread } = useKellyPanel()
  const dispatch = useAppDispatch()
  const { isOpen, surfaceRef, closeNow, triggerHandlers, surfaceHandlers } = useHoverPreview()
  // Anchored to the rail, not to the hovered button: off the button the peek opens inside the rail's padding
  // and clips the icons below. State, not a ref — reading a ref during render is forbidden (react-hooks/refs).
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const onShowConversation = surfaceNav?.onShowConversation
  const handleNewChat = useCallback(() => {
    closeNow()
    dispatch(closeDockPane())
    onShowConversation?.()
    startNewThread()
  }, [closeNow, dispatch, onShowConversation, startNewThread])

  // Only the closed-to-open edge: `open` fires again when the pointer returns during the close delay.
  const analytics = useAnalytics()
  const onOpenSkills = skills?.onOpen
  const isSkillsOpen = skills?.isOpen ?? false
  const onOpenAdminSettings = adminSettings?.onOpen
  const isAdminSettingsOpen = adminSettings?.isOpen ?? false
  const isPaneOpen = isSkillsOpen || isAdminSettingsOpen
  const openSkills = useCallback(() => {
    closeNow()
    if (!isSkillsOpen) analytics.track(SKILL_EVENTS.SKILLS_PAGE_OPENED, { surface: "collapsed_rail" })
    onOpenSkills?.()
  }, [closeNow, analytics, isSkillsOpen, onOpenSkills])
  const openAdminSettings = useCallback(() => {
    closeNow()
    if (!isAdminSettingsOpen) analytics.track(ADMIN_SETTINGS_EVENTS.SETTINGS_PAGE_OPENED, { surface: "collapsed_rail" })
    onOpenAdminSettings?.()
  }, [closeNow, analytics, isAdminSettingsOpen, onOpenAdminSettings])
  const onOpenArtifacts = surfaceNav?.onOpenArtifacts
  const openArtifacts = useCallback(() => {
    closeNow()
    onOpenArtifacts?.()
  }, [closeNow, onOpenArtifacts])
  const isArtifactsActive = (surfaceNav?.isArtifactsActive ?? false) && !isPaneOpen
  const onOpenWorkflows = surfaceNav?.workflows?.onOpen
  const openWorkflows = useCallback(() => {
    closeNow()
    onOpenWorkflows?.()
  }, [closeNow, onOpenWorkflows])
  const isWorkflowsActive = (surfaceNav?.workflows?.isActive ?? false) && !isPaneOpen
  const wasOpenRef = useRef(isOpen)
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) analytics.track(AI_TEAMMATE_EVENTS.RECENTS_PREVIEW_OPENED, { surface: "collapsed_rail" })
    wasOpenRef.current = isOpen
  }, [isOpen, analytics])

  return (
    <Paper ref={setAnchorEl} square elevation={0} sx={RAIL_SX} component="aside" aria-label="Collapsed conversations panel">
      {/* No hover tooltip: it would open where the peek does and fire the preview's leave handlers. */}
      <Tooltip title="Open conversations" disableHoverListener>
        <IconButton
          ref={openButtonRef}
          aria-label="Open conversations panel"
          data-testid="chatbox-rail-expand"
          sx={RAIL_BUTTON_SX}
          onClick={onOpen}
          {...triggerHandlers}
        >
          <ViewSidebarOutlined />
        </IconButton>
      </Tooltip>
      <Tooltip title="New chat" placement="right" disableInteractive>
        <IconButton
          aria-label="New chat"
          data-testid="chatbox-rail-new-chat"
          color="primary"
          onClick={handleNewChat}
        >
          <Add />
        </IconButton>
      </Tooltip>
      {skills && (
        <Tooltip title="Skills" placement="right" disableInteractive>
          <IconButton
            aria-label="Skills"
            aria-current={skills.isOpen ? "page" : undefined}
            data-testid="chatbox-rail-skills"
            sx={skills.isOpen ? RAIL_BUTTON_ACTIVE_SX : RAIL_BUTTON_SX}
            onClick={openSkills}
          >
            <ExtensionOutlined />
          </IconButton>
        </Tooltip>
      )}
      {surfaceNav && (
        <Tooltip title="Artifacts" placement="right" disableInteractive>
          <IconButton
            aria-label="Artifacts"
            aria-current={isArtifactsActive ? "page" : undefined}
            data-testid="chatbox-rail-artifacts"
            sx={isArtifactsActive ? RAIL_BUTTON_ACTIVE_SX : RAIL_BUTTON_SX}
            onClick={openArtifacts}
          >
            <GridViewOutlined />
          </IconButton>
        </Tooltip>
      )}
      {surfaceNav?.workflows && (
        <Tooltip title="Scheduled Workflows" placement="right" disableInteractive>
          <IconButton
            aria-label="Scheduled Workflows"
            aria-current={isWorkflowsActive ? "page" : undefined}
            data-testid="chatbox-rail-workflows"
            sx={isWorkflowsActive ? RAIL_BUTTON_ACTIVE_SX : RAIL_BUTTON_SX}
            onClick={openWorkflows}
          >
            <EventRepeatOutlined />
          </IconButton>
        </Tooltip>
      )}
      {adminSettings && (
        <Tooltip title="Settings" placement="right" disableInteractive>
          <IconButton
            aria-label="Settings"
            aria-current={isAdminSettingsOpen ? "page" : undefined}
            data-testid="chatbox-rail-admin-settings"
            sx={isAdminSettingsOpen ? RAIL_BUTTON_ACTIVE_SX : RAIL_BUTTON_SX}
            onClick={openAdminSettings}
          >
            <AdminPanelSettingsOutlined />
          </IconButton>
        </Tooltip>
      )}
      <ConversationsPreview
        isOpen={isOpen}
        anchorEl={anchorEl}
        placement="right-start"
        surfaceRef={surfaceRef}
        surfaceHandlers={surfaceHandlers}
        onDismiss={closeNow}
        testId="chatbox-rail-preview"
      />
    </Paper>
  )
}
